import { useStore } from 'src/contexts/StoreContext';
import Modal from '../modal';
import { View, StyleProp } from 'react-native';
import { ViewStyle } from '@expo/html-elements/build/primitives/View';
import TextLato from '../../text/text-lato/TextLato';
import { mobileStyle, styles } from './BaseModal.styles';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';
import { ICON_NAMES } from '../../icon/icon.helper';
import { modalTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import { SecondaryButton } from 'src/editor-v3/components/buttons/secondary-button/SecondaryButton';
import { PrimaryButton } from 'src/editor-v3/components/buttons/primary-button/PrimaryButton';
import { BUTTON_LAYOUTS, BUTTON_SIZES, BUTTON_TYPES } from 'src/editor-v3/components/buttons/button/Button.constants';
import { FocusGroups } from '../../focus-provider/FocusProvider.constants';
import { DYNAMIC_MODAL_TYPES } from '../dynamic-modals/DynamicModal.types';
import { IconButton } from 'src/editor-v3/components/icon-buttons/icon-button/IconButton';
import { ButtonProps } from 'src/editor-v3/components/buttons/button/Button.types';

type BaseModalProps = {
  onClose?: () => void;
  onCloseOutside?: () => void;
  modalStyle?: StyleProp<ViewStyle>;
  modalWidth?: number;
  containerStyle?: StyleProp<ViewStyle>;
  modalIcon: React.ReactNode;
  title?: string;
  header?: React.ReactNode;
  description?: string;
  body?: React.ReactNode;
  handleDiscard?: () => void;
  discardText?: string;
  handleConfirm?: () => void;
  confirmText?: string;
  showLoader?: boolean;
  isVisible: boolean;
  testID: TestIDs;
  type: DYNAMIC_MODAL_TYPES;
  id?: string;
  closeOnClickOutside?: boolean;
  primaryButtonProps?: Partial<ButtonProps>;
};

const DEFAULT_MODAL_WIDTH = 384;

const BaseModal = ({
  onClose,
  onCloseOutside,
  modalStyle,
  modalWidth = DEFAULT_MODAL_WIDTH,
  containerStyle,
  modalIcon,
  handleDiscard,
  discardText,
  handleConfirm,
  confirmText,
  title,
  header,
  description,
  body,
  showLoader,
  isVisible,
  testID,
  type,
  id,
  closeOnClickOutside = true,
  primaryButtonProps,
}: BaseModalProps) => {
  const { i18n } = useStore();
  const { isMobile } = useResponsiveScreen();
  const responsiveModalStyle = isMobile
    ? { ...styles, ...mobileStyle, modal: { ...styles.modal, ...mobileStyle.modal, maxWidth: modalWidth } }
    : { ...styles, modal: { ...styles.modal, width: modalWidth } };

  return (
    <Modal
      visible={isVisible}
      onClose={async () => onCloseOutside && onCloseOutside()}
      showHeader={false}
      style={[responsiveModalStyle.modal, modalStyle]}
      style_container={[
        responsiveModalStyle.container,
        !!onClose && responsiveModalStyle.containerWithCloseIcon,
        containerStyle,
      ]}
      closeOnClickOutside={closeOnClickOutside}
      testID={testID}
      type={type}
      id={id}
    >
      <View style={responsiveModalStyle.closeButton}>
        {onClose && (
          <IconButton
            onPress={async () => onClose()}
            accessible={true}
            accessibilityLabel={i18n.t('actions:close', { x: i18n.t('general:modal') })}
            accessibilityRole="button"
            iconName={ICON_NAMES.Close}
            iconStyle={responsiveModalStyle.closeIcon}
            size={BUTTON_SIZES.SMALL}
            testID={modalTestID(testID, TestIDs.CLOSE)}
            focusId={modalTestID(testID, TestIDs.CLOSE)}
            focusOrder={2}
            focusGroup={FocusGroups.MODAL}
          />
        )}
      </View>
      <View>{modalIcon}</View>
      <View
        style={[
          responsiveModalStyle.contentContainer,
          !!modalIcon ? responsiveModalStyle.contentContainerWithMarginTop : {},
          discardText || confirmText ? responsiveModalStyle.contentContainerWithMarginBottom : {},
        ]}
      >
        {header ?? (title && <TextLato style={responsiveModalStyle.title}>{title}</TextLato>)}
        {body ?? (description && <TextLato style={responsiveModalStyle.description}>{description}</TextLato>)}
      </View>
      <View style={responsiveModalStyle.ctaContainer}>
        {discardText && (
          <SecondaryButton
            onPress={async () => handleDiscard && handleDiscard()}
            text={discardText}
            testID={modalTestID(testID, TestIDs.DISCARD)}
            focusId={modalTestID(testID, TestIDs.DISCARD)}
            focusOrder={1}
            focusGroup={FocusGroups.MODAL}
            size={BUTTON_SIZES.REGULAR}
            type={BUTTON_TYPES.DEFAULT}
            layout={BUTTON_LAYOUTS.FILL}
            style={styles.ctaButton}
          />
        )}
        {confirmText && (
          <PrimaryButton
            onPress={async () => handleConfirm && handleConfirm()}
            text={confirmText}
            testID={modalTestID(testID, TestIDs.CONFIRM)}
            focusId={modalTestID(testID, TestIDs.CONFIRM)}
            focusOrder={0}
            focusGroup={FocusGroups.MODAL}
            loading={showLoader}
            size={BUTTON_SIZES.REGULAR}
            type={BUTTON_TYPES.DEFAULT}
            layout={BUTTON_LAYOUTS.FILL}
            style={styles.ctaButton}
            {...(primaryButtonProps ?? {})}
          />
        )}
      </View>
    </Modal>
  );
};

export default BaseModal;
